import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CredentialsFormData,
  createMewsCredentialsSchema,
} from "../../utils/pmsSchemas";
import { PmsSettings, UpdateMewsCredentialsRequest } from "@/types/pms";
import { PmsType } from "@/types/hotel";

interface MewsCredentialsFormProps {
  settings: PmsSettings | null;
  canWrite: boolean;
  isSaving: boolean;
  onSave: (data: UpdateMewsCredentialsRequest) => void;
}

export const MewsCredentialsForm = ({
  settings,
  canWrite,
  isSaving,
  onSave,
}: MewsCredentialsFormProps) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CredentialsFormData>({
    resolver: zodResolver(createMewsCredentialsSchema()),
    defaultValues: {
      clientName: "Guesteria",
      baseUrl: settings?.baseUrl ?? "",
    },
  });

  React.useEffect(() => {
    reset({
      clientName: "Guesteria",
      baseUrl: settings?.baseUrl ?? "",
    });
  }, [reset, settings?.baseUrl]);

  const submit = (data: CredentialsFormData) => {
    onSave({ ...data, provider: PmsType.MEWS });
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor="apiToken">{t("pms.credentials.apiToken")}</Label>
        <Input
          id="apiToken"
          type="password"
          placeholder={t("pms.credentials.apiTokenPlaceholder")}
          disabled={!canWrite}
          {...register("apiToken")}
        />
        {errors.apiToken && (
          <p className="text-destructive text-sm">{errors.apiToken.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="clientToken">{t("pms.credentials.clientToken")}</Label>
        <Input
          id="clientToken"
          type="password"
          placeholder={t("pms.credentials.clientTokenPlaceholder")}
          disabled={!canWrite}
          {...register("clientToken")}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="clientName">{t("pms.credentials.clientName")}</Label>
        <Input
          id="clientName"
          placeholder={t("pms.credentials.clientNamePlaceholder")}
          disabled={!canWrite}
          {...register("clientName")}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="webhookSecret">
          {t("pms.credentials.webhookSecret")}
        </Label>
        <Input
          id="webhookSecret"
          type="password"
          placeholder={t("pms.credentials.webhookSecretPlaceholder")}
          disabled={!canWrite}
          {...register("webhookSecret")}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="baseUrl">{t("pms.credentials.baseUrl")}</Label>
        <Input
          id="baseUrl"
          placeholder={t("pms.credentials.baseUrlPlaceholder")}
          disabled={!canWrite}
          {...register("baseUrl")}
        />
        {errors.baseUrl && (
          <p className="text-destructive text-sm">{errors.baseUrl.message}</p>
        )}
      </div>

      <Button
        type="submit"
        variant="new_default"
        disabled={!canWrite || isSaving}
        className="self-start"
      >
        {isSaving ? t("pms.credentials.saving") : t("pms.credentials.save")}
      </Button>
    </form>
  );
};
